import { computed, observable, action } from 'mobx';
import {
    PaymentStatus,
    IPaymentContent,
    DateCalcType,
    AmountCalcType
} from '~/types/model.types';
import {
    IPaymentVariableValue,
    AutoPayStatus,
    IPaymentBlockResponse
} from '~/types/server.types';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import type { CXPaymentScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-schedule-block.model';
import { Variable } from '~/models/variable.model';
import { toFixedIfNotRound } from '~/utils/money.utils';
import type { CXRecurringScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-recurring-schedule-block.model';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import { getPaymentDateDisplayValue } from '~/components/common/InvoiceBlock/invoice.utils';
import { formatDate } from '~/utils/date.utils';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { calculateLateFee } from '~/components/Payments/utils';

export class CXPaymentBlock extends CXBaseBlock {
    type: 'payment_block';
    @observable payment_content: IPaymentContent;
    @observable date_issued: string;
    @observable connected_variable_id: string;

    constructor(
        block: IPaymentBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    @action
    mergeBlockAttributes(block, attributes) {
        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }

        if (block.payment_content) {
            this.payment_content = block.payment_content;
        }
    }

    get connectedVariable(): Variable<IPaymentVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get totalAmount() {
        return this.paymentAmount + this.lateFee + this.tipPaid;
    }

    @computed
    get isEditable() {
        return false;
    }

    @computed
    get connectedDueDateToShow() {
        return getPaymentDateDisplayValue({
            date: this.paymentDueDate,
            paymentVariable: this.connectedVariable?.value,
            paymentContent: this.payment_content || {
                date_calc_type: DateCalcType.Fixed
            },
            isRecurring: this.invoiceBlock?.isRecurringInvoice
        });
    }

    @computed
    get calculatedDueDateText() {
        let dateToShow = this.connectedDueDateToShow;
        if (
            this.flow.isLeadForm &&
            this.payment_content.date_calc_type === DateCalcType.Today
        ) {
            dateToShow = `${formatDate(new Date(), 'MM D, YYYY')}`;
        }
        if (!!dateToShow && this.isMilestone && !!this.milestoneCompletedOn) {
            dateToShow = `${formatDate(
                this.milestoneCompletedOn,
                'MM D, YYYY'
            )} (${dateToShow})`;
        }
        return !!dateToShow ? dateToShow : '- - -';
    }

    @computed
    get paymentIndex() {
        return this.connectedVariable?.value?.payment_index;
    }

    @computed
    get is_paid() {
        return this.connectedVariable?.value?.is_paid;
    }

    @computed
    get is_refunded() {
        return this.connectedVariable?.value?.is_refunded;
    }

    @computed
    get fetchVariable(): IPaymentVariableValue {
        return this.connectedVariable?.value;
    }

    @computed
    get paymentAmount() {
        if (this.flow.isLeadForm) {
            return this.invoiceBlock.invoiceSummaryBlock.grandTotal;
        }
        return this.connectedVariable?.value?.amount || 0;
    }

    @computed
    get refundedAmount() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return (
            value.refunded_amount ||
            value.pending_mark_as_refunded?.refunded_amount ||
            0
        );
    }

    @computed
    get netPayAmount() {
        return this.isRefunded
            ? this.paymentAmount - this.refundedAmount
            : this.paymentAmount;
    }

    @computed
    get paymentPercents() {
        if (this.isPercentageBased) {
            return toFixedIfNotRound(this.payment_content?.percentage);
        }

        const { totalAmount } = this.paymentScheduleBlock;
        return totalAmount
            ? toFixedIfNotRound((this.paymentAmount / totalAmount) * 100, 0)
            : 0;
    }

    @computed
    get isFixed() {
        return this.amountCalcType === AmountCalcType.Fixed;
    }

    @computed
    get isPercentageBased() {
        return [
            AmountCalcType.Percentage,
            AmountCalcType.DivideEqually
        ].includes(this.amountCalcType);
    }

    @computed
    get amountCalcType() {
        return this.payment_content?.amount_calc_type;
    }

    @computed
    get milestoneTitle() {
        return this.payment_content?.milestone_title;
    }

    @computed
    get paymentDueDate() {
        return this.connectedVariable?.value?.due_date;
    }

    @computed
    get isPaymentDueDateTbd() {
        return this.connectedVariable?.value?.is_tbd;
    }

    @computed
    get paymentChargeId() {
        return this.connectedVariable?.value?.charge_id;
    }

    @computed
    get paymentChargeDate() {
        return this.connectedVariable?.value?.charge_date;
    }

    @computed
    get isAutoPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.is_auto_paid;
    }

    @computed
    get isAutoPay() {
        if (this.isPaid || this.isRefunded) {
            return this.isAutoPaid;
        }
        return this.invoiceBlock.chargeBlock.isAutoPayActive;
    }

    @computed
    get shouldShowAutopayIndication() {
        if (this.isPaid || this.isRefunded) {
            return this.isAutoPaid;
        } else {
            // In the invoice wizard we do not have a charge block, so need a null check
            const autoPayStatus = this.invoiceBlock.chargeBlock?.autoPayStatus;
            return (
                autoPayStatus === AutoPayStatus.Active ||
                autoPayStatus === AutoPayStatus.Paused
            );
        }
    }

    get isOverDue() {
        return this.paymentStatus === PaymentStatus.Overdue;
    }

    @computed
    get invoiceId() {
        return this.connectedVariable?.value?.invoice_id;
    }

    @computed
    get recurringInvoiceNumber() {
        return this.connectedVariable?.value?.invoice_header_snapshot
            ?.invoice_number;
    }

    get paymentPayerEmail() {
        return this.connectedVariable?.value?.payment_payer_email;
    }

    get paymentPayerName() {
        return this.connectedVariable?.value?.payment_payer_name;
    }

    get paymentMethodName() {
        return this.connectedVariable?.value?.payment_method_name;
    }

    get paymentMethodType() {
        return this.connectedVariable?.value?.payment_method_type;
    }

    get paymentMethodTypeName() {
        return this.connectedVariable?.value?.payment_method_type_name;
    }

    get paymentMethodLast4() {
        return this.connectedVariable?.value?.payment_method_last4;
    }

    @computed
    get isPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return (
            !this.isRefunded && (value.is_paid || !!value.pending_mark_as_paid)
        );
    }

    @computed
    get isRefunded() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return !!value.is_refunded || !!value.pending_mark_as_refunded;
    }

    @computed
    get isRefunding() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }

        return !!value.refund_data?.is_client_pending_refund;
    }

    @computed
    get isPartiallyRefunded() {
        return (
            this.paymentAmount &&
            this.refundedAmount &&
            this.paymentAmount > this.refundedAmount
        );
    }

    @computed
    get isCanceled() {
        return (
            this.flow.expired ||
            (this.invoiceBlock.isRecurringInvoice &&
                !this.recurringScheduleBlock.isActive)
        );
    }

    @computed
    get isPending() {
        return this.connectedVariable?.value?.is_pending;
    }

    @computed
    get isMilestone() {
        if (this.payment_content) {
            return (
                this.payment_content.date_calc_type === DateCalcType.Milestone
            );
        }
        return false;
    }

    @computed
    get milestoneCompletedOn() {
        return this.connectedVariable?.value?.milestone_completed_on;
    }

    @computed
    get isUnpaidMilestone() {
        return (
            this.isMilestone &&
            !!this.connectedVariable?.value?.milestone_completed_on &&
            !this.isPaid
        );
    }

    @computed
    get paymentId() {
        return this.connectedVariable?.value?.payment_id;
    }

    @computed
    get isNextPayment() {
        if (this.parent.type === 'payment_schedule_block') {
            return (
                (this.parent as CXPaymentScheduleBlock).nextPaymentBlock
                    ?.block_id === this.block_id
            );
        }
        return (
            (this.parent as CXRecurringScheduleBlock).nextPaymentBlock
                ?.block_id === this.block_id
        );
    }

    @computed
    get paymentStatus() {
        if (
            this.isCanceled &&
            !this.isPaid &&
            !this.isRefunded &&
            !this.isPartiallyRefunded
        ) {
            return PaymentStatus.Canceled;
        }
        if (this.flow.isLeadForm) {
            return PaymentStatus.DueToday;
        }
        const paymentVariable = this.connectedVariable;
        return (
            paymentVariable?.value?.payment_status_label ||
            PaymentStatus.Upcoming
        );
    }

    @computed
    get paymentStatusTracking() {
        return this.connectedVariable?.value?.payment_status_tracking || [];
    }

    @computed
    get chargeDescriptor() {
        return this.connectedVariable?.value?.charge_statement_descriptor;
    }

    get isManualPaid() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return false;
        }
        return value.is_paid && value.is_manual;
    }

    @computed
    get depositDate() {
        return this.connectedVariable?.value?.estimated_deposit_date;
    }

    @computed
    get allowInstantPayout() {
        return this.connectedVariable?.value?.allow_instant_payout;
    }

    get invoiceBlock() {
        return this.paymentScheduleBlock.parent as CXInvoiceBlock;
    }

    get paymentScheduleBlock() {
        return this.parent as CXPaymentScheduleBlock;
    }

    get recurringScheduleBlock() {
        return this.parent as CXRecurringScheduleBlock;
    }

    @computed
    get tipPaid() {
        // see payment-block.model.ts#L301
        if (this.isPaid || this.isPending || this.isRefunded) {
            return this.connectedVariable?.value?.tip_paid || 0;
        }
        return 0;
    }

    @computed
    get lateFee() {
        return calculateLateFee(this);
    }

    @computed
    get isLateFeeWaived() {
        return !!this.connectedVariable?.value?.late_fee_waived_ts;
    }

    @computed
    get currency() {
        return this.flow.currency;
    }

    @computed
    get paymentRefundDate() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.last_refund_timestamp;
    }

    @computed
    get refundId() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }

        return value.refund_data?.refund_transaction_id;
    }

    @computed
    get transactionId() {
        const value = this.connectedVariable?.value;
        if (!value) {
            return null;
        }
        return value.charge_id;
    }
}
